// Putting files into a document body: by paste, by drop, or by the insert
// function (`pickFiles` / `insertFiles`, which the slash menu calls).
//
// Nothing is put into the document while a file uploads, not even a
// decoration. `@tiptap/y-tiptap` applies remote changes as a replacement of
// the whole document (`tr.replace(0, doc.content.size, …)`); a widget inside
// that range is mapped away, so a placeholder would vanish as soon as someone
// else typed a character. What is in flight shows in a meter at the place it
// will go (file-upload-meters.tsx): name and progress only.
//
// The place is remembered as a Yjs relative position (`rememberDocPosition` /
// `resolveDocPosition`): plain positions jump with that replacement, relative
// ones keep pointing at the same place. If the place itself is gone, the file
// goes to the end of the body (never silently dropped). The meter reads the
// same token, so it stays put while others edit above it.
//
// State lives in the extension's storage; the screen reads it with
// `subscribe` + `useSyncExternalStore` (no props -> state copies).
import { type Editor, Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { fileEmbedOf } from "@trameria/core/files-shared";
import {
  rememberDocPosition,
  resolveDocPosition,
  type TrameriaFileAttrs,
} from "@trameria/editor";
import type { Api } from "../../data/api";
import {
  imageDimensions,
  type UploadHandle,
  uploadErrorMessage,
  uploadFile,
  uploadMimeOf,
} from "./upload";

/** One file in a meter. Finished ones leave, so these are in flight or failed. */
export interface PendingFileUpload {
  key: string;
  name: string;
  /**
   * Bytes (`File.size`). Shown only while in flight: a chip in the body never
   * shows a size (it would need the file's meta, which the static view cannot
   * fetch, and the look would change at the switch).
   */
  size: number;
  /** 0..1. */
  progress: number;
  /** Why it failed; null while in flight. */
  error: string | null;
  /**
   * Where it will go in the current document (where the meter is drawn), or
   * null when that place is gone. Not rounded to the edges like the insert
   * is: the meter then sits at the visible edge of the area.
   */
  pos(): number | null;
  /** Cancels (aborts the PUT and drops the row). */
  cancel(): void;
}

export interface FileUploadStorage {
  uploads: PendingFileUpload[];
  /** Lets the person choose files and puts them in. `range` (the slash menu's "/…") is deleted first. */
  pick(range?: { from: number; to: number }): void;
  /** Puts files at hand (pasted, dropped) at `pos`. */
  start(files: File[], pos: number): void;
  /** For the meters (`useSyncExternalStore`). */
  subscribe(listener: () => void): () => void;
}

declare module "@tiptap/core" {
  interface Storage {
    fileUpload?: FileUploadStorage;
  }
}

/**
 * Where to insert. `resolved` is `resolveDocPosition`'s answer: null when the
 * place is gone, and then the end of the body (never dropped silently). Kept
 * within the document in case it shrank.
 */
export function insertPosition(
  resolved: number | null,
  docSize: number,
): number {
  if (resolved === null) return docSize;
  return Math.min(Math.max(resolved, 0), docSize);
}

/**
 * Where the next file of one batch goes. The first goes to its own token; the
 * rest go right after the previous one. All at one token would pile up and
 * reverse (Yjs relative positions lean left, so the later insert comes
 * first; it showed with inline chips). Kept as a token too: the second may
 * finish seconds later, with edits above it meanwhile.
 */
interface Batch {
  /** One of the batch went in (until then each uses its own token). */
  done: boolean;
  /** Right after the previous one; null in an editor without collaboration. */
  token: Uint8Array | null;
  /** The plain position for an editor without tokens. */
  pos: number;
}

/**
 * The content to insert at `at`: an image or a video is a block
 * (`trameriaFile`) plus an empty paragraph, anything else an inline chip
 * (`trameriaFileInline`).
 *
 * An inline node only fits inside a paragraph. When `at` is not inside one
 * (between blocks, at the end, a gone place moved to the end), it is wrapped
 * in a paragraph here. Unwrapped, the schema would silently drop it: the
 * uploaded file would appear nowhere, never be bound to the document, and be
 * purged a day later.
 */
export function inlineOrBlock(
  editor: Editor,
  at: number,
  attrs: TrameriaFileAttrs,
): Record<string, unknown>[] {
  if (attrs.image)
    return [{ type: "trameriaFile", attrs }, { type: "paragraph" }];
  // A chip has no width (only body images have one).
  const chip = {
    type: "trameriaFileInline",
    attrs: { fileId: attrs.fileId, name: attrs.name, image: attrs.image },
  };
  return editor.state.doc.resolve(at).parent.isTextblock
    ? [chip]
    : [{ type: "paragraph", content: [chip] }];
}

/**
 * `addStorage` gets no `editor` (its `this` has only name, options and
 * parent), so the real functions are installed here from `onBeforeCreate`.
 * The storage object is filled in, not replaced, so the swapped `uploads`
 * stays visible from outside.
 */
function installFileUploads(
  storage: FileUploadStorage,
  editor: Editor,
  api: Api,
): void {
  const listeners = new Set<() => void>();

  function setUploads(next: PendingFileUpload[]): void {
    storage.uploads = next;
    for (const listener of listeners) listener();
  }
  function patch(key: string, values: Partial<PendingFileUpload>): void {
    setUploads(
      storage.uploads.map((u) => (u.key === key ? { ...u, ...values } : u)),
    );
  }
  function remove(key: string): void {
    setUploads(storage.uploads.filter((u) => u.key !== key));
  }

  /**
   * Inserts at the remembered place: images and videos as blocks, the rest as
   * inline chips (the one rule the syntax, the node views and the static view
   * share). Only images take a whole line; chips sit inside a paragraph, so
   * several files can share a line from the screen too.
   *
   * What comes with it differs: a block gets an empty paragraph (inserted
   * alone, the selection would be the atom and the next keystroke would
   * replace it); a chip does not (the caret lands after it, ready to type on).
   */
  function insert(
    token: Uint8Array | null,
    fallback: number,
    attrs: TrameriaFileAttrs,
  ): number | null {
    const size = editor.state.doc.content.size;
    // Without collaboration there is no token: the plain position from the start.
    const resolved =
      token === null ? fallback : resolveDocPosition(editor.state, token);
    const at = insertPosition(resolved, size);
    editor
      .chain()
      .insertContentAt(at, inlineOrBlock(editor, at, attrs))
      .run();
    // After the inserted content (`insertContentAt` moves the selection
    // there). The next of the batch starts here - for chips only: a block's
    // selection is inside the empty paragraph added with it, and starting the
    // second image there would eat the blank line between them. Blocks never
    // overlap (each pushes the rest down), so their own tokens keep the order.
    return attrs.image ? null : editor.state.selection.from;
  }

  /**
   * Uploads one file. The promise says this one is settled; the next of the
   * batch waits for it before inserting (`storage.start`).
   */
  function startOne(
    file: File,
    pos: number,
    after: Promise<void>,
    batch: Batch,
  ): Promise<void> {
    const key = crypto.randomUUID();
    const token = rememberDocPosition(editor.state, pos);
    const attrs: TrameriaFileAttrs = {
      fileId: "",
      name: file.name,
      // Image or video is decided by the type alone, by the same function the
      // agent's upload and MCP's import use (`fileEmbedOf`): the form is baked
      // into the body when inserted and cannot be fixed later.
      ...fileEmbedOf(uploadMimeOf(file)),
    };
    let handle: UploadHandle | null = null;
    let cancelled = false;

    setUploads([
      ...storage.uploads,
      {
        key,
        name: file.name,
        size: file.size,
        progress: 0,
        error: null,
        // The same token the insert uses; the plain position without collaboration.
        pos: () =>
          editor.isDestroyed
            ? null
            : token === null
              ? pos
              : resolveDocPosition(editor.state, token),
        cancel: () => {
          cancelled = true;
          handle?.abort();
          remove(key);
        },
      },
    ]);

    // frontend-development: a write started by a paste / pick event, not a fetch in an effect.
    return (async () => {
      const dimensions = await imageDimensions(file);
      // Cancelled or left while measuring: no PUT.
      if (cancelled || editor.isDestroyed) return;
      handle = uploadFile(api, file, {
        onProgress: (ratio) => {
          if (!cancelled) patch(key, { progress: ratio });
        },
        dimensions,
      });
      try {
        const ready = await handle.promise;
        // Wait for the previous one (the order is the order chosen).
        await after;
        if (cancelled || editor.isDestroyed) return;
        remove(key);
        const from = batch.done
          ? { token: batch.token, fallback: batch.pos }
          : { token, fallback: pos };
        const end = insert(from.token, from.fallback, {
          ...attrs,
          fileId: ready.id,
        });
        if (end !== null) {
          batch.done = true;
          batch.pos = end;
          batch.token = rememberDocPosition(editor.state, end);
        }
      } catch (error) {
        // Failures and cancels do not wait: they insert nothing, and the
        // reason shows at once. A cancel already removed the row.
        if (cancelled || editor.isDestroyed) return;
        patch(key, { error: uploadErrorMessage(error) });
      }
    })();
  }

  storage.subscribe = (listener) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  storage.start = (files, pos) => {
    // In the order chosen. Uploads run in parallel, and inserting in the order
    // they finish would put small files first; only the inserts are serial.
    const batch: Batch = { done: false, token: null, pos };
    let previous = Promise.resolve();
    for (const file of files) previous = startOne(file, pos, previous, batch);
  };

  storage.pick = (range) => {
    // The "/…" text goes before choosing (as with the other slash items): the
    // document moves while the dialog is open, so the range would be stale later.
    if (range) editor.chain().focus().deleteRange(range).run();
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.addEventListener("change", () => {
      const files = Array.from(input.files ?? []);
      if (files.length === 0) return;
      // From the selection left where the text was deleted: it follows remote
      // edits made while the dialog was open.
      storage.start(files, editor.state.selection.from);
    });
    input.click();
  };
}

const fileDropKey = new PluginKey("trameriaFileDrop");

export const FileUpload = Extension.create<
  { api: Api | null },
  FileUploadStorage
>({
  name: "fileUpload",

  addOptions() {
    return { api: null };
  },

  addStorage() {
    // Filled in by `onBeforeCreate` (see `installFileUploads`). The shape is
    // returned now because whether `editor.storage.fileUpload` exists is how
    // menus decide to offer "file".
    return {
      uploads: [],
      pick: () => {},
      start: () => {},
      subscribe: () => () => {},
    };
  },

  // `onBeforeCreate`, not `onCreate`: TipTap emits `create` in a
  // `setTimeout(…, 0)` at the end of the Editor constructor, which would leave
  // one tick with an editor but no functions. `beforeCreate` is synchronous in
  // the constructor; only the `editor` reference is kept until pick / start run.
  onBeforeCreate() {
    if (this.options.api)
      installFileUploads(this.storage, this.editor, this.options.api);
  },

  addProseMirrorPlugins() {
    const storage = this.storage;
    return [
      new Plugin({
        key: fileDropKey,
        props: {
          handlePaste(view, event) {
            // Files on the clipboard (any type, not only images) go up.
            const files = Array.from(event.clipboardData?.files ?? []);
            if (files.length === 0) return false;
            storage.start(files, view.state.selection.from);
            // The text is not thrown away: a rich copy may carry HTML and a
            // file together, so with text present the normal paste runs too
            // (this schema has no image node, so an `<img>` in the HTML drops
            // and the text stays; the file comes in as its own node). With
            // nothing else (a screenshot), true: false would fall back to
            // prosemirror-view's hidden paste target and let the browser touch
            // the body.
            const data = event.clipboardData;
            return (
              (data?.getData("text/html") ?? "") === "" &&
              (data?.getData("text/plain") ?? "") === ""
            );
          },
          handleDrop(view, event, _slice, moved) {
            // A block moved inside the editor is ProseMirror's.
            if (moved || view.dragging) return false;
            const files = Array.from(
              (event as DragEvent).dataTransfer?.files ?? [],
            );
            if (files.length === 0) return false;
            const at = view.posAtCoords({
              left: (event as DragEvent).clientX,
              top: (event as DragEvent).clientY,
            });
            event.preventDefault();
            storage.start(files, at?.pos ?? view.state.selection.from);
            return true;
          },
        },
      }),
    ];
  },

  // An editor made again (its Y.Doc changed) gets new storage, and running
  // PUTs would lose where they go: they are stopped, not left running.
  // `cancel` aborts and removes the row; `remove` swaps the array, so the one
  // looped over stays intact.
  onDestroy() {
    for (const upload of this.storage.uploads) upload.cancel();
  },
});

/**
 * Lets the person choose files and puts them where the caret is. For the slash
 * menu: `range` is its "/…" text, deleted first. False when this editor cannot
 * take files (no upload extension).
 */
export function pickFiles(
  editor: Editor,
  range?: { from: number; to: number },
): boolean {
  const uploads = editor.storage.fileUpload;
  if (!uploads) return false;
  uploads.pick(range);
  return true;
}

/** Uploads files and puts them at `pos` (default: the caret). */
export function insertFiles(
  editor: Editor,
  files: File[],
  pos?: number,
): boolean {
  const uploads = editor.storage.fileUpload;
  if (!uploads || files.length === 0) return false;
  uploads.start(files, pos ?? editor.state.selection.from);
  return true;
}
